import {
  computed, defineComponent, ref, watch, type PropType,
} from 'vue';
import {
  VBtn,
  VCard,
  VCardActions,
  VCardText,
  VCardTitle,
  VChip,
  VDialog,
  VDivider,
  VIcon,
  VLayout, VList, VListItem, VNavigationDrawer,
  VSheet,
} from 'vuetify/components';
import html from './MfeDrawer.html'; // eslint-disable-line no-restricted-imports

export interface LogEntry {
  data?: object;
  level: string;
  message: string;
  name?: string;
  source?: string;
  timestamp: number;
  raw?: object;
}

export const MfeDrawer = defineComponent({
  name: 'MfeDrawer',
  template: html,
  components: {
    VCard,
    VBtn,
    VCardText,
    VCardTitle,
    VChip,
    VCardActions,
    VIcon,
    VLayout,
    VList,
    VDialog,
    VListItem,
    VNavigationDrawer,
    VSheet,
    VDivider,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array as PropType<LogEntry[]>,
      default: () => [],
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const seenEventsUntil = ref<number>(0);
    const removeEventsUntil = ref<number>(0);
    const rawFormattedEvent = ref<string>('');
    const showRawEventDialog = ref<boolean>(false);

    //
    // Computed
    //
    const model = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value),
    });

    const itemsToDisplay = computed(() => props.items
      .filter((item) => item.timestamp > removeEventsUntil.value)
      .map((item) => ({
        ...item,
        border: `${item.level} lg`,
        jsonString: item.data ? JSON.stringify(item.data, null, 2) : null,
        hasBeenSeen: item.timestamp <= seenEventsUntil.value,
      }))
      .sort((a: LogEntry, b: LogEntry) => (a.timestamp > b.timestamp ? -1 : 1)));

    //
    // Functions
    //
    function formatDate(date: number) {
      return new Date(date).toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 2,
      });
    }

    function markAllEventsAsSeen() {
      removeEventsUntil.value = itemsToDisplay.value[0].timestamp;
    }

    function showRawEvent(logItem: LogEntry) {
      rawFormattedEvent.value = JSON.stringify(logItem.raw, null, 2);
      showRawEventDialog.value = true;
    }

    //
    // Watcher
    //

    // Mark all seen events as not being "new" anymore
    watch(() => props.modelValue, (currentValue) => {
      if (currentValue === false && itemsToDisplay.value.length > 0) {
        seenEventsUntil.value = itemsToDisplay.value[0].timestamp;
      }
    }, { flush: 'post' }); // Execute after DOM update

    return {
      markAllEventsAsSeen,
      model,
      formatDate,
      itemsToDisplay,
      showRawEvent,
      rawFormattedEvent,
      showRawEventDialog,
    };
  },
});
